import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { s_GC } from '../../shared/styles';
import { useStorage } from '../../shared/hooks';
import { loadGameHistory, weekBoundaries, avgScoreInWindow, lastNScores } from '../../shared/utils';
import Sparkline from '../../components/ui/Sparkline';

const SCORE_TIERS = [
  { min:0,  max:15,  label:"Awakening",   labelNL:"Ontwaken",      color:"#6B7A99", desc:"You're beginning to activate your mental capabilities.",         descNL:"Je begint je mentale vermogens te activeren." },
  { min:16, max:35,  label:"Developing",  labelNL:"Groeiend",      color:"#5B9ED0", desc:"Your brain is forming new neural pathways through practice.",    descNL:"Je brein vormt nieuwe neurale paden door oefening." },
  { min:36, max:55,  label:"Sharpening",  labelNL:"Verscherpend",  color:"#2A9E82", desc:"Noticeable improvements in daily focus and reaction speed.",     descNL:"Merkbare verbeteringen in dagelijkse focus en reactiesnelheid." },
  { min:56, max:75,  label:"Proficient",  labelNL:"Bedreven",      color:"#C8A84B", desc:"Your focus is becoming a reliable tool in complex situations.",  descNL:"Je focus wordt een betrouwbaar instrument in complexe situaties." },
  { min:76, max:100, label:"Elite",        labelNL:"Elite",         color:"#C8A84B", desc:"You operate with elite-level mental clarity and control.",       descNL:"Je opereert met elite-niveau mentale helderheid en controle." },
];

function FocusStats({ lang }) {
  const nl = lang === "nl";
  const [exHistory] = useStorage("sl_ex_history", []);
  const [expanded, setExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const mathHist         = loadGameHistory("math");
  const reactHist        = loadGameHistory("reaction");
  const memHist          = loadGameHistory("memory");
  const seqHist          = loadGameHistory("sequence");
  const stroopHist       = loadGameHistory("stroop");
  const focusTapHist     = loadGameHistory("focustap");
  const oddSymbolHist    = loadGameHistory("oddsymbol");
  const dirFlashHist     = loadGameHistory("directionflash");
  const periphFlashHist  = loadGameHistory("peripheralflash");
  const numChainHist     = loadGameHistory("numchain");
  const wordMemHist      = loadGameHistory("wordmemory");

  const thisWeek = weekBoundaries(0);
  const lastWeek = weekBoundaries(1);
  const avgW = (hist, w, s, e) => hist.length ? (avgScoreInWindow(hist, s, e) ?? w) : null;

  const getPillarScores = (s, e) => {
    const cVals = [
      stroopHist.length    ? Math.min((avgW(stroopHist,    0,s,e)??0),20)/20 : null,
      focusTapHist.length  ? Math.min((avgW(focusTapHist,  0,s,e)??0),20)/20 : null,
      oddSymbolHist.length ? Math.min((avgW(oddSymbolHist, 0,s,e)??0),80)/80 : null,
    ].filter(v=>v!==null);
    const concentration = cVals.length ? Math.round((cVals.reduce((a,b)=>a+b,0)/cVals.length)*25) : 0;

    const rVals = [
      reactHist.length       ? Math.min(Math.max((avgW(reactHist,      600,s,e)??600)/800,0),1) : null,
      dirFlashHist.length    ? Math.min(Math.max((avgW(dirFlashHist,   600,s,e)??600)/800,0),1) : null,
      periphFlashHist.length ? Math.min(Math.max((avgW(periphFlashHist,600,s,e)??600)/800,0),1) : null,
    ].filter(v=>v!==null);
    const reaction = rVals.length ? Math.round((rVals.reduce((a,b)=>a+b,0)/rVals.length)*25) : 0;

    const mVals = [
      memHist.length     ? Math.min(Math.max((avgW(memHist,   60,s,e)??60)/80,0),1)  : null,
      seqHist.length     ? Math.min((avgW(seqHist,    0,s,e)??0),15)/15              : null,
      wordMemHist.length ? Math.min((avgW(wordMemHist,0,s,e)??0),20)/20              : null,
    ].filter(v=>v!==null);
    const memory = mVals.length ? Math.round((mVals.reduce((a,b)=>a+b,0)/mVals.length)*25) : 0;

    const cogVals = [
      mathHist.length     ? Math.min((avgW(mathHist,    0,s,e)??0),40)/40   : null,
      numChainHist.length ? Math.min((avgW(numChainHist,0,s,e)??0),800)/800 : null,
    ].filter(v=>v!==null);
    const cognition = cogVals.length ? Math.round((cogVals.reduce((a,b)=>a+b,0)/cogVals.length)*25) : 0;

    return { concentration, reaction, memory, cognition };
  };

  const pillars     = getPillarScores(thisWeek.start, thisWeek.end);
  const prevPillars = getPillarScores(lastWeek.start, lastWeek.end);
  const focusScore  = pillars.concentration + pillars.reaction + pillars.memory + pillars.cognition;
  const prevScore   = prevPillars.concentration + prevPillars.reaction + prevPillars.memory + prevPillars.cognition;
  const scoreDelta  = focusScore - prevScore;
  const tier        = SCORE_TIERS.find(t => focusScore >= t.min && focusScore <= t.max) || SCORE_TIERS[0];

  const allHistArrays = [mathHist,reactHist,memHist,seqHist,stroopHist,focusTapHist,oddSymbolHist,dirFlashHist,periphFlashHist,numChainHist,wordMemHist];
  const totalSessions = allHistArrays.reduce((a,h)=>a+h.filter(e=>e.ts>=thisWeek.start).length,0);
  const totalAllTime  = allHistArrays.reduce((a,h)=>a+h.length,0);
  const hasAnyData    = totalAllTime > 0;

  const pillarDefs = [
    { key:"concentration", icon:"🎯", label:nl?"Concentratie":"Concentration", color:"#D4873A",
      desc:nl?"Vermogen om afleidingen te blokkeren en bij één taak te blijven.":"Ability to block distractions and stay focused on one task.",
      games:nl?"Stroop · Focus Tap · Vreemd Symbool":"Stroop · Focus Tap · Odd Symbol" },
    { key:"reaction",      icon:"⚡", label:nl?"Reactievermogen":"Reaction",    color:"#C8A84B",
      desc:nl?"Snelheid en nauwkeurigheid van je mentale reacties.":"Speed and accuracy of your mental responses.",
      games:nl?"Reactietijd · Richting Flash · Perifeer":"Reaction · Direction Flash · Peripheral" },
    { key:"memory",        icon:"🧠", label:nl?"Geheugen":"Memory",             color:"#7B6FD0",
      desc:nl?"Capaciteit om informatie vast te houden en op te roepen.":"Capacity to hold and retrieve information.",
      games:nl?"Geheugen · Reeks · Woordgeheugen":"Memory · Sequence · Word Memory" },
    { key:"cognition",     icon:"➕", label:nl?"Cognitie":"Cognition",           color:"#5B9ED0",
      desc:nl?"Mentale rekenkracht en logisch redeneren onder druk.":"Mental arithmetic and logical reasoning under pressure.",
      games:nl?"Cognitie · Nummerketen":"Cognition · Num. Chain" },
  ];

  // Sparkline trend last 7 days
  const focusSparkPoints = Array.from({length:7},(_,i)=>{
    const dayMs=24*3600*1000, end=Date.now()-(6-i)*dayMs, start=end-dayMs;
    const entries=allHistArrays.flat().filter(e=>e.ts>=start&&e.ts<end);
    if(!entries.length) return i===6?focusScore:null;
    return Math.round(entries.reduce((a,e)=>a+e.score/100,0)/entries.length*10);
  });
  const filledFocus = focusSparkPoints.map((v,i,arr)=>{
    if(v!==null) return v;
    const prev=arr.slice(0,i).reverse().find(x=>x!==null);
    const next=arr.slice(i+1).find(x=>x!==null);
    return prev??next??0;
  });

  // Per-game stats for detailed view
  const buildStat = (hist,id,icon,labelNL,labelEN,color,display,better)=>{
    const raw=lastNScores(hist,8);
    const thisAvg=avgScoreInWindow(hist,thisWeek.start,thisWeek.end);
    const lastAvg=avgScoreInWindow(hist,lastWeek.start,lastWeek.end);
    const latest=hist.length?hist[hist.length-1].score:null;
    let displayValue="–",deltaNum=null,deltaLabel="";
    if(latest!==null){
      if(display==="reaction"){displayValue=`${1000-latest}ms`;if(thisAvg!==null&&lastAvg!==null){deltaNum=Math.round(thisAvg-lastAvg);deltaLabel=deltaNum>0?`${Math.round(1000-lastAvg)-Math.round(1000-thisAvg)}ms ${nl?"sneller":"faster"}`:`${Math.round(1000-thisAvg)-Math.round(1000-lastAvg)}ms ${nl?"langzamer":"slower"}`;}}
      else if(display==="memory"){displayValue=`${100-latest} ${nl?"zetten":"moves"}`;if(thisAvg!==null&&lastAvg!==null){deltaNum=Math.round(thisAvg-lastAvg);deltaLabel=deltaNum>0?`${Math.round(100-lastAvg)-Math.round(100-thisAvg)} ${nl?"zetten minder":"fewer moves"}`:`${Math.round(100-thisAvg)-Math.round(100-lastAvg)} ${nl?"zetten meer":"more moves"}`;}}
      else{displayValue=id==="sequence"?`Lv ${Math.round(latest)}`:`${Math.round(latest)}`;if(thisAvg!==null&&lastAvg!==null){deltaNum=Math.round(thisAvg-lastAvg);deltaLabel=deltaNum>0?`+${deltaNum} ${nl?"deze week":"this week"}`:`${deltaNum} ${nl?"deze week":"this week"}`;}}
    }
    const sparkPoints=display==="reaction"?raw.map(v=>1000-v):display==="memory"?raw.map(v=>100-v):raw;
    const sessions=hist.filter(e=>e.ts>=thisWeek.start).length;
    return{id,icon,label:nl?labelNL:labelEN,color,displayValue,sparkPoints,deltaNum,deltaLabel,sessions,hasData:hist.length>0,better};
  };

  const gameStats = [
    buildStat(stroopHist,     "stroop",         "🎯","Stroop",          "Stroop",         "#D4873A","higher",null),
    buildStat(focusTapHist,   "focustap",        "🎯","Focus Tap",       "Focus Tap",      "#4AC87A","higher",null),
    buildStat(oddSymbolHist,  "oddsymbol",       "🔍","Vreemd Symbool",  "Odd Symbol",     "#D4873A","higher",null),
    buildStat(memHist,        "memory",          "🧠","Geheugen",        "Memory",         "#7B6FD0","memory",nl?"lager=beter":"lower=better"),
    buildStat(seqHist,        "sequence",        "🔢","Werkgeheugen",    "Working Mem.",   "#2A9E82","higher",null),
    buildStat(wordMemHist,    "wordmemory",      "📝","Woordgeheugen",   "Word Memory",    "#7B6FD0","higher",null),
    buildStat(reactHist,      "reaction",        "⚡","Reactietijd",     "Reaction",       "#C8A84B","reaction",nl?"lager=beter":"lower=better"),
    buildStat(dirFlashHist,   "directionflash",  "🧭","Richting Flash",  "Dir. Flash",     "#C06090","reaction",nl?"lager=beter":"lower=better"),
    buildStat(periphFlashHist,"peripheralflash", "👁", "Perifeer",       "Peripheral",     "#C8A84B","reaction",nl?"lager=beter":"lower=better"),
    buildStat(mathHist,       "math",            "➕","Cognitie",        "Cognition",      "#5B9ED0","higher",null),
    buildStat(numChainHist,   "numchain",        "🔗","Nummerketen",     "Num. Chain",     "#5B9ED0","higher",null),
  ];

  const bestImproved = gameStats.filter(s=>s.deltaNum>0&&s.hasData).sort((a,b)=>b.deltaNum-a.deltaNum)[0];
  const strongestPillar = pillarDefs.filter(p=>pillars[p.key]>0).sort((a,b)=>pillars[b.key]-pillars[a.key])[0];

  const SECTIONS = [
    {
      id:"what", icon:"❓",
      title: nl?"Wat betekent je Focus Score?":"What does your Focus Score mean?",
      content: nl
        ? "Je Focus Score (0–100) meet vier mentale dimensies: concentratie, reactievermogen, geheugen en cognitie. Elke dimensie is max. 25 punten. De score weerspiegelt je prestaties in echte game-sessies — niet een zelfbeoordeling, maar gemeten mentale kracht. Hoe hoger de score, hoe sterker je mentale gereedschapskist."
        : "Your Focus Score (0–100) measures four mental dimensions: concentration, reaction, memory, and cognition. Each dimension is worth 25 points. The score reflects your performance in real game sessions — not self-assessment, but measured mental strength. The higher the score, the stronger your mental toolkit."
    },
    {
      id:"develop", icon:"📈",
      title: nl?"Wat heb je ontwikkeld?":"What have you developed?",
      content: (() => {
        if(!hasAnyData) return nl?"Speel games om te zien wat je hebt ontwikkeld.":"Play games to see what you've developed.";
        const lines=[];
        if(strongestPillar){
          lines.push(nl
            ?`Je sterkste pilaar is ${strongestPillar.label} (${pillars[strongestPillar.key]}/25 pt). ${strongestPillar.desc}`
            :`Your strongest pillar is ${strongestPillar.label} (${pillars[strongestPillar.key]}/25 pts). ${strongestPillar.desc}`);
        }
        if(bestImproved){
          lines.push(nl
            ?`Je snelst groeiende vaardigheid deze week: ${bestImproved.label}. Consistent trainen bouwt echte neurale verbindingen die je prestaties buiten het spel verbeteren.`
            :`Your fastest-growing skill this week: ${bestImproved.label}. Consistent training builds real neural connections that improve your performance outside the game.`);
        }
        if(totalAllTime>10){
          lines.push(nl
            ?`Met ${totalAllTime} sessies totaal heb je al een solide mentale trainingsbasis opgebouwd die dagelijks zijn vruchten afwerpt.`
            :`With ${totalAllTime} total sessions, you've already built a solid mental training foundation that pays dividends daily.`);
        }
        return lines.length ? lines.join(' ') : (nl?"Blijf trainen om je ontwikkeling te zien.":"Keep training to see your development.");
      })()
    },
    {
      id:"effect", icon:"✨",
      title: nl?"Wat doet het voor jou?":"What does it do for you?",
      content: nl
        ? "Een hogere Focus Score betekent: sneller schakelen tussen taken, minder mentale vermoeidheid, betere beslissingen onder druk, en een rustigere geest in drukke situaties. Focus training herstructureert letterlijk je hersenen — neuroplasticiteit zorgt ervoor dat elke sessie een permanente verbetering achterlaat. Op lange termijn: meer veerkracht, minder stress-reacties, en een groter gevoel van controle over je aandacht."
        : "A higher Focus Score means: faster task-switching, less mental fatigue, better decisions under pressure, and a calmer mind in busy situations. Focus training literally restructures your brain — neuroplasticity ensures every session leaves a permanent improvement. Long-term: more resilience, fewer stress reactions, and a greater sense of control over your attention."
    },
    {
      id:"friends", icon:"👥",
      title: nl?"Train samen, groei sneller":"Train together, grow faster",
      content: nl
        ? "Door samen te spelen met vrienden activeer je sociale cognitie — een extra mentale laag boven individuele focus. Uitdaging creëert gezonde druk die je focus verdiept. Je kunt elkaars Focus Scores vergelijken, doelen stellen en via Focus Sync real-time mentale oefeningen doen. Onderzoek toont aan dat sociale training de motivatie verhoogt en langer volgehouden wordt. Een vriend met een hogere Focus Score kan jou naar nieuwe niveaus duwen die je alleen nooit zou bereiken."
        : "Training with friends activates social cognition — an extra mental layer above individual focus. Challenge creates healthy pressure that deepens your focus. You can compare Focus Scores, set shared goals, and do real-time mental exercises via Focus Sync. Research shows social training increases motivation and is sustained longer. A friend with a higher Focus Score can push you to new levels you'd never reach alone."
    },
  ];

  return (
    <div style={{...s_GC({marginBottom:14}),padding:0,overflow:"hidden",border:`1px solid ${_T.cardBorderBot}`}}>
      {/* Header */}
      <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${_T.cardBorderBot}`}}>
        <div style={{fontSize:12,color:_T.accent,letterSpacing:3,fontWeight:600}}>🧠 {nl?"JE FOCUS":"JE FOCUS"}</div>
        <button onClick={()=>setExpanded(e=>!e)} style={{background:"none",border:"none",color:C.silver,cursor:"pointer",fontSize:12}}>
          {expanded?(nl?"Minder ▲":"Less ▲"):(nl?"Meer ▼":"More ▼")}
        </button>
      </div>

      {/* Hero score */}
      <div style={{padding:"16px",background:`linear-gradient(135deg,${tier.color}14,transparent)`,borderBottom:`1px solid ${_T.cardBorderBot}`}}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={{flexShrink:0}}>
            <svg width={80} height={80}>
              <defs>
                <linearGradient id="fsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={_T.grad1}/><stop offset="100%" stopColor={_T.grad2}/>
                </linearGradient>
              </defs>
              <circle cx={40} cy={40} r={32} fill="none" stroke={_T.bg3} strokeWidth={7}/>
              <circle cx={40} cy={40} r={32} fill="none" stroke="url(#fsGrad)" strokeWidth={7}
                strokeDasharray={2*Math.PI*32}
                strokeDashoffset={2*Math.PI*32*(1-Math.min(focusScore,100)/100)}
                strokeLinecap="round" transform="rotate(-90 40 40)"
                style={{transition:"stroke-dashoffset 0.8s ease"}}/>
              <text x={40} y={45} textAnchor="middle" fill={C.white} fontSize={20} fontWeight="700" fontFamily="Inter">{focusScore}</text>
            </svg>
          </div>
          <div style={{flex:1}}>
            <div style={{color:tier.color,fontSize:15,fontWeight:700,letterSpacing:1,marginBottom:3}}>{nl?tier.labelNL:tier.label}</div>
            <div style={{color:C.silver,fontSize:11,lineHeight:1.5,marginBottom:8}}>{nl?tier.descNL:tier.desc}</div>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {scoreDelta!==0&&(
                <span style={{fontSize:11,color:scoreDelta>0?C.teal:C.orange,fontWeight:700,background:scoreDelta>0?`${C.teal}15`:`${C.orange}15`,padding:"2px 8px",borderRadius:10}}>
                  {scoreDelta>0?"↑":"↓"} {Math.abs(scoreDelta)} {nl?"vs vorige week":"vs last week"}
                </span>
              )}
              <div style={{marginLeft:"auto"}}>
                <Sparkline points={filledFocus} color={tier.color} width={60} height={24}/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pillar bars */}
      <div style={{padding:"14px 16px"}}>
        {pillarDefs.map(p=>{
          const val=pillars[p.key]; const prev=prevPillars[p.key]; const delta=val-prev;
          return (
            <div key={p.key} style={{marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <span style={{fontSize:13}}>{p.icon}</span>
                  <span style={{fontSize:12,color:C.silver2,fontWeight:500}}>{p.label}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {delta!==0&&<span style={{fontSize:10,color:delta>0?C.teal:C.orange,fontWeight:700}}>{delta>0?"+":""}{delta}</span>}
                  <span style={{fontSize:12,color:val>0?p.color:C.silver,fontWeight:700}}>{val}/25</span>
                </div>
              </div>
              <div style={{height:5,background:_T.bg4,borderRadius:3,overflow:"hidden"}}>
                <div style={{width:`${(val/25)*100}%`,height:"100%",background:`linear-gradient(90deg,${p.color}77,${p.color})`,borderRadius:3,transition:"width 0.7s ease"}}/>
              </div>
              <div style={{fontSize:9,color:`${C.silver}88`,marginTop:2}}>{p.games}</div>
            </div>
          );
        })}

        {totalSessions>0&&(
          <div style={{fontSize:11,color:C.silver,textAlign:"center",marginTop:4}}>
            {totalSessions} {nl?"sessies deze week":"sessions this week"} · {totalAllTime} {nl?"totaal":"total"}
          </div>
        )}

        {!hasAnyData&&(
          <div style={{textAlign:"center",padding:"10px 0",color:C.silver,fontSize:12}}>
            {nl?"Speel een game om je statistieken te zien 🎮":"Play a game to see your statistics 🎮"}
          </div>
        )}
      </div>

      {/* Expandable insight sections */}
      {expanded&&(
        <div style={{borderTop:`1px solid ${_T.cardBorderBot}`}}>
          {SECTIONS.map(sec=>(
            <div key={sec.id} style={{borderBottom:`1px solid ${_T.cardBorderBot}`}}>
              <button
                onClick={()=>setActiveSection(activeSection===sec.id?null:sec.id)}
                style={{width:"100%",background:activeSection===sec.id?`${_T.accent}0D`:"transparent",border:"none",padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,textAlign:"left"}}
              >
                <span style={{fontSize:15}}>{sec.icon}</span>
                <span style={{color:C.white,fontSize:12,fontWeight:600,flex:1}}>{sec.title}</span>
                <span style={{color:C.silver,fontSize:12}}>{activeSection===sec.id?"▲":"▼"}</span>
              </button>
              {activeSection===sec.id&&(
                <div style={{padding:"0 16px 14px",background:_T.bg3}}>
                  <div style={{color:C.silver2,fontSize:12,lineHeight:1.8}}>{sec.content}</div>
                </div>
              )}
            </div>
          ))}

          {/* Game stats grid */}
          <div style={{padding:"14px 16px"}}>
            <div style={{fontSize:10,color:C.silver,letterSpacing:2,marginBottom:10}}>{nl?"INDIVIDUELE GAME STATISTIEKEN":"INDIVIDUAL GAME STATISTICS"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              {gameStats.map(s=>(
                <div key={s.id} style={{background:_T.bg4,borderRadius:12,padding:"11px 12px",border:`1px solid ${s.color}22`,opacity:s.hasData?1:0.5}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5}}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:13}}>{s.icon}</span>
                      <span style={{fontSize:11,color:s.color,fontWeight:600}}>{s.label}</span>
                    </div>
                    {s.sessions>0&&<span style={{fontSize:9,color:C.silver,background:`${s.color}15`,padding:"1px 5px",borderRadius:6}}>{s.sessions}x</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:4}}>
                    <div>
                      <div style={{fontSize:17,color:s.hasData?C.white:C.silver,fontWeight:700,lineHeight:1}}>{s.displayValue}</div>
                      {s.better&&<div style={{fontSize:9,color:C.silver,marginTop:1}}>{s.better}</div>}
                    </div>
                    {s.sparkPoints.length>=2
                      ?<Sparkline points={s.sparkPoints} color={s.color} width={48} height={22}/>
                      :<span style={{fontSize:9,color:C.silver}}>{nl?"meer sessies":"more sessions"}</span>
                    }
                  </div>
                  {s.deltaNum!==null&&s.deltaNum!==0
                    ?<div style={{display:"flex",alignItems:"center",gap:3}}>
                      <span style={{fontSize:11,color:s.deltaNum>0?C.teal:C.orange,fontWeight:700}}>{s.deltaNum>0?"↑":"↓"}</span>
                      <span style={{fontSize:9,color:C.silver}}>{s.deltaLabel}</span>
                    </div>
                    :s.hasData
                      ?<div style={{fontSize:9,color:C.silver}}>{nl?"Speel vaker voor trends":"Play more for trend data"}</div>
                      :<div style={{fontSize:9,color:C.silver}}>{nl?"Nog geen data":"No data yet"}</div>
                  }
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FocusStats;
